// CategoryBuilder.jsx
import React, { useState, useEffect } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";

// Sample questions data (in a real app, this would come from an API)
const sampleQuestions = [
  {
    id: "q1",
    type: "likert",
    question: "How satisfied are you with our product?",
    rationale: "This question helps measure overall customer satisfaction and can be used to track changes over time.",
    whenToUse: "Use this question at the end of a customer feedback survey or after a customer has used your product for at least 30 days.",
    author: "Customer Success Team"
  },
  {
    id: "q2",
    type: "dropdown",
    question: "Which feature do you use most frequently?",
    rationale: "Identifies the most valuable features for users and helps prioritize future development.",
    whenToUse: "Use in product feedback surveys or when planning feature enhancements.",
    author: "Product Team"
  },
  {
    id: "q3", 
    type: "text",
    question: "What improvements would you suggest for our service?",
    rationale: "Collects qualitative feedback that might reveal unexpected issues or opportunities.",
    whenToUse: "Use in comprehensive feedback surveys or when you want to gather ideas for innovation.",
    author: "UX Research Team"
  },
  {
    id: "q4",
    type: "slider",
    question: "How likely are you to recommend our product to a colleague?",
    rationale: "Measures Net Promoter Score (NPS), a key indicator of customer loyalty and satisfaction.",
    whenToUse: "Use in short satisfaction surveys or when tracking customer loyalty metrics.",
    author: "Marketing Team"
  },
  {
    id: "q5",
    type: "likert",
    question: "The product meets all my requirements.",
    rationale: "Assesses how well the product fulfills user needs and expectations.",
    whenToUse: "Include in product evaluation surveys or after major feature releases.",
    author: "Product Management"
  }
];

const QuestionLibraryModal = ({ isOpen, onClose, componentTypes, onAddQuestion, categories }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState(sampleQuestions.slice(0, 3));

  // Handle search input changes
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (term.trim() === "") {
      // Show first 3 questions when search is empty
      setSearchResults(sampleQuestions.slice(0, 3));
    } else {
      // Filter questions based on search term
      const filteredResults = sampleQuestions.filter(q => 
        q.question.toLowerCase().includes(term.toLowerCase()) ||
        q.type.toLowerCase().includes(term.toLowerCase()) ||
        q.rationale.toLowerCase().includes(term.toLowerCase()) ||
        q.author.toLowerCase().includes(term.toLowerCase())
      );
      setSearchResults(filteredResults.slice(0, 3)); // Limit to 3 results
    }
  };

  // Handle adding a question to the survey
  const handleAddQuestion = (question) => {
    // Create a copy of the question to avoid modifying the original
    const questionToAdd = {...question};
    
    // If it's a dropdown question and we have categories available
    if (questionToAdd.type === 'dropdown' && categories) {
      // Find a date category if one exists
      const dateCategory = categories.find(cat => cat.type === 'date');
      if (dateCategory) {
        // Store the category and its type in the question
        questionToAdd.category = dateCategory.name;
        questionToAdd.categoryType = 'date';
      }
    }
    
    onAddQuestion(questionToAdd);
    onClose();
  };

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Question Library</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        
        <SearchContainer>
          <SearchInput 
            type="text" 
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search questions by type, content, or author..."
            autoFocus
          />
        </SearchContainer>
        
        <QuestionsContainer>
          {searchResults.length === 0 ? (
            <EmptyMessage>No questions found. Try a different search term.</EmptyMessage>
          ) : (
            searchResults.map((question) => (
              <QuestionCard key={question.id}>
                <QuestionType>{question.type}</QuestionType>
                <QuestionText>{question.question}</QuestionText>
                
                <DetailSection>
                  <DetailLabel>Rationale:</DetailLabel>
                  <DetailText>{question.rationale}</DetailText>
                </DetailSection>
                
                <DetailSection>
                  <DetailLabel>When to use:</DetailLabel>
                  <DetailText>{question.whenToUse}</DetailText>
                </DetailSection>
                
                <DetailSection>
                  <DetailLabel>Author:</DetailLabel>
                  <DetailText>{question.author}</DetailText>
                </DetailSection>
                
                <ActionContainer>
                  <AddButton onClick={() => handleAddQuestion(question)}>
                    Add to Survey
                  </AddButton>
                </ActionContainer>
              </QuestionCard>
            ))
          )}
        </QuestionsContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

// Styled components
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-in-out;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 80%;
  max-width: 800px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.3s ease-in-out;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  font-family: "Raleway", sans-serif;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #888;
  &:hover {
    color: #333;
  }
`;

const SearchContainer = styled.div`
  padding: 20px;
  border-bottom: 1px solid #eee;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  font-family: "Raleway", sans-serif;
  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const QuestionsContainer = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
`;

const QuestionCard = styled.div`
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s, transform 0.2s;
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const QuestionType = styled.div`
  display: inline-block;
  background-color: #f0f7ff;
  color: #4a90e2;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  text-transform: capitalize;
  margin-bottom: 8px;
`;

const QuestionText = styled.h3`
  margin: 8px 0 16px;
  font-size: 1.1rem;
  font-weight: 600;
`;

const DetailSection = styled.div`
  margin-bottom: 12px;
`;

const DetailLabel = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 4px;
  color: #555;
`;

const DetailText = styled.div`
  font-size: 0.9rem;
  color: #666;
  line-height: 1.4;
`;

const ActionContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  clear: both;
`;

const AddButton = styled.button`
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #3a7bc8;
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #666;
  font-size: 1rem;
`;

export default QuestionLibraryModal;